import Utils from "./utils";
import { LogManagerImpl, LogLevel, Logger } from "./log";
import {
    MIN_WEBSOCKET_LIFETIME_MS,
    WEBSOCKET_URL_VALID_TIME_MS,
    HEARTBEAT_INTERVAL_MS,
    ROUTE_KEY,
    CONN_STATE,
    MAX_CONSECUTIVE_FAILED_SUB_ATTEMPTS,
    TOPIC_SUBSCRIPTION_RETRY_INTERVAL_MS,
    MAX_WAIT_TIME_SUB_REQUEST_WITH_NO_RESPONSE_MS,
    MAX_CONSECUTIVE_SUB_REQUEST_WITH_NO_RESPONSE,
    NETWORK_CONN_CHECK_INTERVAL_MS,
    WEBSOCKET_REINIT_JITTER,
    LOG_MESSAGES,
} from "./constants";
import { RetryProvider } from './retryProvider';

let logManagerInstance = null;
const WebSocketManager = function () {

    const logger = logManagerInstance.getLogger({ prefix: LOG_MESSAGES.DEFAULT_PREFIX, excludeTimestamp: true });

    let online = Utils.isNetworkOnline();

    let webSocket = {
        primary: null,
        secondary: null
    };

    let reconnectConfig = {
        reconnectWebSocket: true,
        websocketInitFailed: false,
        exponentialBackOffTime: 1000,
        exponentialTimeoutHandle: null,
        lifeTimeTimeoutHandle: null,
        webSocketInitCheckerTimeoutId: null,
        connState: null
    };

    let metrics = {
        connectWebSocketRetryCount: 0,
        connectionAttemptStartTime: null,
        noOpenConnectionsTimestamp: null
    };

    let heartbeatConfig = {
        pendingResponse: false,
        intervalHandle: null
    };

    let deepHeartbeatConfig = {
        pendingResponse: false,
        intervalHandle: null
    };

    let callbacks = {
        initFailure: new Set(),
        getWebSocketTransport: null,
        subscriptionUpdate: new Set(),
        subscriptionFailure: new Set(),
        topic: new Map(),
        allMessage: new Set(),
        connectionGain: new Set(),
        connectionLost: new Set(),
        connectionOpen: new Set(),
        connectionClose: new Set(),
        deepHeartbeatSuccess: new Set(),
        deepHeartbeatFailure: new Set(),
        topicFailure: new Set(),
    };

    let webSocketConfig = {
        connConfig: null,
        promiseHandle: null,
        promiseCompleted: true
    };

    let topicSubscription = {
        subscribed: new Set(),
        pending: new Set(),
        subscriptionHistory: new Set()
    };

    let topicSubscriptionConfig = {
        responseCheckIntervalId: null,
        requestCompleted: true,
        reSubscribeIntervalId: null,
        consecutiveFailedSubscribeAttempts: 0,
        consecutiveNoResponseRequest: 0
    };

    const reconnectionClient = new RetryProvider(() => { getWebSocketConnConfig().catch(() => {}); });

    const invalidSendMessageRouteKeys = new Set([ROUTE_KEY.SUBSCRIBE, ROUTE_KEY.UNSUBSCRIBE, ROUTE_KEY.HEARTBEAT, ROUTE_KEY.DEEP_HEARTBEAT]);

    const networkConnectivityChecker = setInterval(function () {
        if (online !== Utils.isNetworkOnline()) {
            online = Utils.isNetworkOnline();
            if (!online) {
                sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.NETWORK_OFFLINE));
                return;
            }
            const ws = getDefaultWebSocket();
            if (online && (!ws || isWebSocketState(ws, WebSocket.CLOSING) || isWebSocketState(ws, WebSocket.CLOSED))) {
                sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.NETWORK_ONLINE));
                getWebSocketConnConfig().catch(() => {});
            }
        }
    }, NETWORK_CONN_CHECK_INTERVAL_MS);

    const invokeCallbacks = function (callbacks, response) {
        callbacks.forEach(function (callback) {
            try {
                callback(response);
            } catch (error) {
                sendInternalLogToServer(logger.error("Error executing callback", error));
            }
        });
    };

    const getWebSocketStates = function (ws) {
        if (ws === null) return "NULL";
        switch (ws.readyState) {
            case WebSocket.CONNECTING:
                return "CONNECTING";
            case WebSocket.OPEN:
                return "OPEN";
            case WebSocket.CLOSING:
                return "CLOSING";
            case WebSocket.CLOSED:
                return "CLOSED";
            default:
                return "UNDEFINED";
        }
    };

    const printWebSocketState = function (event = "") {
        sendInternalLogToServer(logger.debug("[" + event + "] Primary WebSocket: " + getWebSocketStates(webSocket.primary)
            + " | " + "Secondary WebSocket: " + getWebSocketStates(webSocket.secondary)));
    };

    const isWebSocketState = function (ws, webSocketStateCode) {
        return ws && ws.readyState === webSocketStateCode;
    };

    const isWebSocketOpen = function (ws) {
        return isWebSocketState(ws, WebSocket.OPEN);
    };

    const isWebSocketClosed = function (ws) {
        // undefined check is to address the limitation of testing framework
        return ws === null || ws.readyState === undefined || isWebSocketState(ws, WebSocket.CLOSED);
    };

    /**
     * This function is meant to handle the scenario when we have two web-sockets open
     * in such a scenario we always select secondary web-socket since all future operations
     * are supposed to be done by this secondary web-socket
     */
    const getDefaultWebSocket = function () {
        if (webSocket.secondary !== null) {
            return webSocket.secondary;
        }
        return webSocket.primary;
    };

    const isDefaultWebSocketOpen = function () {
        return isWebSocketOpen(getDefaultWebSocket());
    };

    const sendHeartBeat = function () {
        if (deepHeartbeatConfig.pendingResponse) {
            sendInternalLogToServer(logger.debug(LOG_MESSAGES.NO_DEEP_HEARTBEAT));
            // trigger deepHeartbeatFailure - no response is received 
            invokeCallbacks(callbacks.deepHeartbeatFailure, {
                timestamp: Date.now(),
                error: "aws/ping response is not received",
            });

            clearInterval(deepHeartbeatConfig.intervalHandle);
            deepHeartbeatConfig.pendingResponse = false;
        }
        if (heartbeatConfig.pendingResponse) {
            sendInternalLogToServer(logger.warn(LOG_MESSAGES.NO_HEARTBEAT));

            clearInterval(heartbeatConfig.intervalHandle);
            heartbeatConfig.intervalHandle = null;
            heartbeatConfig.pendingResponse = false;
            getWebSocketConnConfig().catch(() => {});
            return;
        }
        if (isDefaultWebSocketOpen()) {
            sendInternalLogToServer(logger.debug(LOG_MESSAGES.SENDING_DEEP_HEARTBEAT));
            getDefaultWebSocket().send(createWebSocketPayload(ROUTE_KEY.DEEP_HEARTBEAT));
            deepHeartbeatConfig.pendingResponse = true;

            sendInternalLogToServer(logger.debug(LOG_MESSAGES.SENDING_HEARTBEAT));
            getDefaultWebSocket().send(createWebSocketPayload(ROUTE_KEY.HEARTBEAT));
            heartbeatConfig.pendingResponse = true;
        } else {
            sendInternalLogToServer(logger.debug(LOG_MESSAGES.FAILED_DEEP_HEARTBEAT));
            // trigger deepHeartbeatFailure - message is unable to be sent to aws/ping because websocket connection is not established.
            invokeCallbacks(callbacks.deepHeartbeatFailure, {
                timestamp: Date.now(),
                error: "Unable to send message to aws/ping because websocket connection is not established."
            });

            sendInternalLogToServer(logger.warn(LOG_MESSAGES.FAILED_HEARTBEAT));

            printWebSocketState("sendHeartBeat");
            getWebSocketConnConfig().catch(() => {});
        }
    };

    const resetWebSocketState = function () {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_RESET));
        reconnectConfig.exponentialBackOffTime = 1000;
        heartbeatConfig.pendingResponse = false;
        deepHeartbeatConfig.pendingResponse = false;

        reconnectConfig.reconnectWebSocket = true;

        clearTimeout(reconnectConfig.lifeTimeTimeoutHandle);
        clearInterval(heartbeatConfig.intervalHandle);
        clearInterval(deepHeartbeatConfig.intervalHandle);
        clearTimeout(reconnectConfig.exponentialTimeoutHandle);
        clearTimeout(reconnectConfig.webSocketInitCheckerTimeoutId);

        heartbeatConfig.intervalHandle = null;
    };

    const resetSubscriptions = function () {
        topicSubscriptionConfig.consecutiveFailedSubscribeAttempts = 0;
        topicSubscriptionConfig.consecutiveNoResponseRequest = 0;
        clearInterval(topicSubscriptionConfig.responseCheckIntervalId);
        clearInterval(topicSubscriptionConfig.reSubscribeIntervalId);
    };

    const resetMetrics = function () {
        metrics.connectWebSocketRetryCount = 0;
        metrics.connectionAttemptStartTime = null;
        metrics.noOpenConnectionsTimestamp = null;
    };

    const webSocketOnOpen = function () {
        // Mark connection as successful; reset the number of reconnect attempts to 0.
        reconnectionClient.connected();

        try {
            sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_ESTABLISHED));

            printWebSocketState("webSocketOnOpen");
            if (reconnectConfig.connState === null || reconnectConfig.connState === CONN_STATE.DISCONNECTED) {
                invokeCallbacks(callbacks.connectionGain);
            }
            reconnectConfig.connState = CONN_STATE.CONNECTED;

            // Report number of retries to open and record ws open time
            const now = Date.now();
            invokeCallbacks(callbacks.connectionOpen, {
                connectWebSocketRetryCount: metrics.connectWebSocketRetryCount,
                connectionAttemptStartTime: metrics.connectionAttemptStartTime,
                noOpenConnectionsTimestamp: metrics.noOpenConnectionsTimestamp,
                connectionEstablishedTime: now,
                timeToConnect: now - metrics.connectionAttemptStartTime,
                timeWithoutConnection:
                    metrics.noOpenConnectionsTimestamp ? now - metrics.noOpenConnectionsTimestamp : null
            });

            resetMetrics();
            resetWebSocketState();
            getDefaultWebSocket().openTimestamp = Date.now(); // record open time

            // early closure of primary web socket
            if (topicSubscription.subscribed.size === 0 && isWebSocketOpen(webSocket.secondary)) {
                closeSpecificWebSocket(webSocket.primary, "[Primary WebSocket] Closing WebSocket");
            }
            if (topicSubscription.subscribed.size > 0 || topicSubscription.pending.size > 0) {
                if (isWebSocketOpen(webSocket.secondary)) {
                    sendInternalLogToServer(logger.info("Subscribing secondary websocket to topics of primary websocket"));
                }
                topicSubscription.subscribed.forEach(topic => {
                    topicSubscription.subscriptionHistory.add(topic);
                    topicSubscription.pending.add(topic);
                });
                topicSubscription.subscribed.clear();
                subscribePendingTopics();
            }

            sendHeartBeat();
            if (heartbeatConfig.intervalHandle !== null) {
                clearInterval(heartbeatConfig.intervalHandle);
            }
            heartbeatConfig.intervalHandle = setInterval(sendHeartBeat, HEARTBEAT_INTERVAL_MS);

            const webSocketLifetimeTimeout = webSocketConfig.connConfig.webSocketTransport.transportLifeTimeInSeconds * 1000;
            sendInternalLogToServer(logger.debug("Scheduling WebSocket manager reconnection, after delay " + webSocketLifetimeTimeout + " ms"));

            reconnectConfig.lifeTimeTimeoutHandle = setTimeout(function () {
                sendInternalLogToServer(logger.debug("Starting scheduled WebSocket manager reconnection"));

                getWebSocketConnConfig().catch(() => {});
            }, webSocketLifetimeTimeout);
        } catch (error) {
            sendInternalLogToServer(logger.error("Error after establishing WebSocket connection", error));
        }
    };

    const webSocketOnClose = function (event, ws) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_CLOSED, JSON.stringify(event)));

        printWebSocketState("webSocketOnClose before-cleanup");

        invokeCallbacks(callbacks.connectionClose, {
            openTimestamp: ws.openTimestamp,
            closeTimestamp: Date.now(),
            connectionDuration: Date.now() - ws.openTimestamp,
            code: event.code,
            reason: event.reason
        });

        if (isWebSocketClosed(webSocket.primary)) {
            webSocket.primary = null;
        }
        if (isWebSocketClosed(webSocket.secondary)) {
            webSocket.secondary = null;
        }
        if (!reconnectConfig.reconnectWebSocket) {
            return;
        }
        if (!isWebSocketOpen(webSocket.primary) && !isWebSocketOpen(webSocket.secondary)) {
            sendInternalLogToServer(logger.warn("Neither primary websocket and nor secondary websocket have open connections, attempting to re-establish connection"));

            if (reconnectConfig.connState === CONN_STATE.DISCONNECTED) {
                /**
                 * This check is required in the scenario where WS Server shuts-down and closes all active
                 * WS Client connections and WS Server takes about a minute to become active again, in this
                 * scenario WS Client's onClose is triggered and then WSM start reconnect logic immediately but all
                 * connect request to WS Server would fail and WS Client's onError callback would be triggered
                 * followed WS Client's onClose callback and hence "connectionLost" callback would be invoked several
                 * times and this behavior is redundant
                 */
                sendInternalLogToServer(logger.info("Ignoring connectionLost callback invocation"));
            } else {
                invokeCallbacks(callbacks.connectionLost, {
                    openTimestamp: ws.openTimestamp,
                    closeTimestamp: Date.now(),
                    connectionDuration: Date.now() - ws.openTimestamp,
                    code: event.code,
                    reason: event.reason
                });
                metrics.noOpenConnectionsTimestamp = Date.now();
            }
            reconnectConfig.connState = CONN_STATE.DISCONNECTED;
            getWebSocketConnConfig().catch(() => {});
        } else if (isWebSocketClosed(webSocket.primary) && isWebSocketOpen(webSocket.secondary)) {
            sendInternalLogToServer(logger.debug("[Primary] WebSocket Cleanly Closed"));

            webSocket.primary = webSocket.secondary;
            webSocket.secondary = null;
        }
        printWebSocketState("webSocketOnClose after-cleanup");
    };

    const webSocketOnError = function (event) {
        printWebSocketState("webSocketOnError");
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_ERROR, JSON.stringify(event)));
        const isConnected = reconnectionClient.getIsConnected();

        if (isConnected) {
            getWebSocketConnConfig().catch(() => {});
        } else {
            reconnectionClient.retry();
        }
    };

    const webSocketOnMessage = function (event) {
        if (typeof event.data === "undefined" || event.data === "") {
            sendInternalLogToServer(logger.warn("An empty message has been received on Websocket. Ignoring"));
            return;
        }
        const response = JSON.parse(event.data);

        switch (response.topic) {

            case ROUTE_KEY.SUBSCRIBE: {
                sendInternalLogToServer(logger.debug("Subscription Message received from webSocket server"));

                topicSubscriptionConfig.requestCompleted = true;
                topicSubscriptionConfig.consecutiveNoResponseRequest = 0;

                if (response.content.status === "success") {
                    topicSubscriptionConfig.consecutiveFailedSubscribeAttempts = 0;
                    response.content.topics.forEach(topicName => {
                        topicSubscription.subscriptionHistory.delete(topicName);
                        topicSubscription.pending.delete(topicName);
                        topicSubscription.subscribed.add(topicName);
                    });
                    if (topicSubscription.subscriptionHistory.size === 0) {
                        if (isWebSocketOpen(webSocket.secondary)) {
                            sendInternalLogToServer(logger.debug("Successfully subscribed secondary websocket to all topics of primary websocket"));

                            closeSpecificWebSocket(webSocket.primary, "[Primary WebSocket] Closing WebSocket");
                        }
                    } else {
                        subscribePendingTopics();
                    }
                    invokeCallbacks(callbacks.subscriptionUpdate, response);

                } else {
                    clearInterval(topicSubscriptionConfig.reSubscribeIntervalId);
                    ++topicSubscriptionConfig.consecutiveFailedSubscribeAttempts;
                    if (topicSubscriptionConfig.consecutiveFailedSubscribeAttempts === MAX_CONSECUTIVE_FAILED_SUB_ATTEMPTS) {
                        invokeCallbacks(callbacks.subscriptionFailure, response);
                        topicSubscriptionConfig.consecutiveFailedSubscribeAttempts = 0;
                        return;
                    }
                    topicSubscriptionConfig.reSubscribeIntervalId = setInterval(function () {
                        subscribePendingTopics();
                    }, TOPIC_SUBSCRIPTION_RETRY_INTERVAL_MS);
                }
                break;
            }
            case ROUTE_KEY.HEARTBEAT: {
                sendInternalLogToServer(logger.debug(LOG_MESSAGES.HEARTBEAT_RECEIVED));
                heartbeatConfig.pendingResponse = false;
                if (heartbeatConfig.intervalHandle === null) {
                    heartbeatConfig.intervalHandle = setInterval(sendHeartBeat, HEARTBEAT_INTERVAL_MS);
                }
                break;
            }
            case ROUTE_KEY.DEEP_HEARTBEAT: {
                sendInternalLogToServer(logger.debug(LOG_MESSAGES.DEEP_HEARTBEAT_RECEIVED));
                deepHeartbeatConfig.pendingResponse = false;
                
                if (response.statusCode === 200) {// trigger deepHeartbeatSuccess if 200 response is received
                    invokeCallbacks(callbacks.deepHeartbeatSuccess, {
                        timestamp: Date.now(),
                    });
                } else {//trigger deepHeartbeatFailure if 500 response is received
                    invokeCallbacks(callbacks.deepHeartbeatFailure, {
                        timestamp: Date.now(),
                        statusCode: response.statusCode,
                        statusContent: response.statusContent,
                    });
                }
                break;
            }
            default: {
                if (response.topic) {
                    sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_MESSAGE_RECEIVED, response.topic));

                    if (isWebSocketOpen(webSocket.primary) && isWebSocketOpen(webSocket.secondary)
                        && topicSubscription.subscriptionHistory.size === 0 && this === webSocket.primary) {
                        /**
                         * This block is to handle scenario when both primary and secondary socket have subscribed to
                         * a common topic but we are facing difficulty in closing the primary socket, then in this
                         * situation messages will be received by both primary and secondary web socket
                         */
                        sendInternalLogToServer(logger.warn("Ignoring Message for Topic " + response.topic + ", to avoid duplicates"));

                        return;
                    }

                    if (callbacks.allMessage.size === 0 && callbacks.topic.size === 0) {
                        sendInternalLogToServer(logger.warn('No registered callback listener for Topic', response.topic));

                        return;
                    }
                    sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_MESSAGE_SUCCESS, response.topic));
                    invokeCallbacks(callbacks.allMessage, response);
                    if (callbacks.topic.has(response.topic)) {
                        invokeCallbacks(callbacks.topic.get(response.topic), response);
                    }

                } else if (response.message) {
                    sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_MESSAGE_ERROR, response));

                    // trigger topicFailure if Forbidden message OR Timeout message is received
                    invokeCallbacks(callbacks.topicFailure, {
                        timestamp: Date.now(),
                        errorMessage: response.message,
                        connectionId: response.connectionId,
                        requestId: response.requestId,
                    });
                } else {
                    sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_MESSAGE_INVALID, response));
                }
            }
        }
    };

    const subscribePendingTopics = function () {
        if (topicSubscriptionConfig.consecutiveNoResponseRequest > MAX_CONSECUTIVE_SUB_REQUEST_WITH_NO_RESPONSE) {
            sendInternalLogToServer(logger.warn("Ignoring subscribePendingTopics since we have exhausted max subscription retries with no response"));

            invokeCallbacks(callbacks.subscriptionFailure, Utils.getSubscriptionResponse(ROUTE_KEY.SUBSCRIBE, false, Array.from(topicSubscription.pending)));
            return;
        }
        if (!isDefaultWebSocketOpen()) {
            sendInternalLogToServer(logger.warn("Ignoring subscribePendingTopics call since Default WebSocket is not open"));

            return;
        }
        if (Array.from(topicSubscription.pending).length === 0) {
            return;
        }

        clearInterval(topicSubscriptionConfig.responseCheckIntervalId);

        getDefaultWebSocket().send(createWebSocketPayload(ROUTE_KEY.SUBSCRIBE, {
            "topics": Array.from(topicSubscription.pending)
        }));
        topicSubscriptionConfig.requestCompleted = false;

        // This callback ensure that some response was received for subscription request
        topicSubscriptionConfig.responseCheckIntervalId = setInterval(function () {
            if (!topicSubscriptionConfig.requestCompleted) {
                ++topicSubscriptionConfig.consecutiveNoResponseRequest;
                subscribePendingTopics();
            }
        }, MAX_WAIT_TIME_SUB_REQUEST_WITH_NO_RESPONSE_MS);
    };

    const closeSpecificWebSocket = function (ws, reason) {
        if (isWebSocketState(ws, WebSocket.CONNECTING) || isWebSocketState(ws, WebSocket.OPEN)) {
            ws.close(1000, reason);
        } else {
            sendInternalLogToServer(logger.warn("Ignoring WebSocket Close request, WebSocket State: " + getWebSocketStates(ws)));
        }
    };

    const closeWebSocket = function (reason) {
        closeSpecificWebSocket(webSocket.primary, "[Primary] WebSocket " + reason);
        closeSpecificWebSocket(webSocket.secondary, "[Secondary] WebSocket " + reason);
    };

    const retryWebSocketInitialization = function () {
        metrics.connectWebSocketRetryCount++;
        const waitTime = Utils.addJitter(reconnectConfig.exponentialBackOffTime, WEBSOCKET_REINIT_JITTER);
        if (Date.now() + waitTime <= webSocketConfig.connConfig.urlConnValidTime) {
            sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_REINITIALIZATION + waitTime + " ms"));

            reconnectConfig.exponentialTimeoutHandle = setTimeout(() => initWebSocket(), waitTime);
            reconnectConfig.exponentialBackOffTime *= 2;
        } else {
            sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_REINITIALIZATION_TIMEOUT));

            getWebSocketConnConfig().catch(() => {});
        }
    };

    const terminateWebSocketManager = function (response) {
        resetWebSocketState();
        resetSubscriptions();
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_INITIALIZATION_FAILED, response));

        reconnectConfig.websocketInitFailed = true;
        closeWebSocket(LOG_MESSAGES.WEBSOCKET_TERMINATED);
        clearInterval(networkConnectivityChecker);
        invokeCallbacks(callbacks.initFailure, {
            connectWebSocketRetryCount: metrics.connectWebSocketRetryCount,
            connectionAttemptStartTime: metrics.connectionAttemptStartTime,
            reason: response
        });
        resetMetrics();
    };

    const createWebSocketPayload = function (key, content) {
        return JSON.stringify({
            "topic": key,
            "content": content
        });
    };

    const sendMessage = function (payload) {
        Utils.assertIsObject(payload, "payload");
        if (payload.topic === undefined || invalidSendMessageRouteKeys.has(payload.topic)) {
            sendInternalLogToServer(logger.warn("Cannot send message, Invalid topic: " + payload.topic));

            return;
        }
        try {
            payload = JSON.stringify(payload);
        } catch (error) {
            sendInternalLogToServer(logger.warn("Error stringify message", payload));

            return;
        }
        if (isDefaultWebSocketOpen()) {
            getDefaultWebSocket().send(payload);
        } else {
            sendInternalLogToServer(logger.warn("Cannot send message, web socket connection is not open"));
        }
    };

    const subscribeTopics = function (topics) {
        Utils.assertNotNull(topics, 'topics');
        Utils.assertIsList(topics);

        topics.forEach(topic => {
            if (!topicSubscription.subscribed.has(topic)) {
                topicSubscription.pending.add(topic);
            }
        });
        // This ensure all participant-request to subscribe to topic chat are served at least once
        topicSubscriptionConfig.consecutiveNoResponseRequest = 0;
        subscribePendingTopics();
    };

    const validWebSocketConnConfig = function (connConfig) {
        if (Utils.isObject(connConfig) && Utils.isObject(connConfig.webSocketTransport)
            && Utils.isNonEmptyString(connConfig.webSocketTransport.url)
            && Utils.validWSUrl(connConfig.webSocketTransport.url) &&
            connConfig.webSocketTransport.transportLifeTimeInSeconds * 1000 >= MIN_WEBSOCKET_LIFETIME_MS) {
            return true;
        }
        sendInternalLogToServer(logger.error("Invalid WebSocket Connection Configuration", connConfig));

        return false;
    };

    const getWebSocketConnConfig = function () {
        if (!Utils.isNetworkOnline()) {
            sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.NETWORK_OFFLINE_WARNING));
            return Promise.resolve({ webSocketConnectionFailed: true });
        }
        if (reconnectConfig.websocketInitFailed) {
            sendInternalLogToServer(logger.debug("WebSocket Init had failed, ignoring this getWebSocketConnConfig request"));
            return Promise.resolve({ webSocketConnectionFailed: true });
        }
        if (!webSocketConfig.promiseCompleted) {
            sendInternalLogToServer(logger.debug("There is an ongoing getWebSocketConnConfig request, this request will be ignored"));
            return Promise.resolve({ webSocketConnectionFailed: true });
        }
        resetWebSocketState();
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_NEW_CONNECTION));

        metrics.connectionAttemptStartTime = metrics.connectionAttemptStartTime || Date.now();
        webSocketConfig.promiseCompleted = false;
        webSocketConfig.promiseHandle = callbacks.getWebSocketTransport();
        return webSocketConfig.promiseHandle
            .then(function (response) {
                webSocketConfig.promiseCompleted = true;
                sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_SUCCESS));

                if (!validWebSocketConnConfig(response)) {
                    terminateWebSocketManager("Invalid WebSocket connection configuration: " + response);
                    return { webSocketConnectionFailed: true };
                }
                webSocketConfig.connConfig = response;
                // Ideally this URL validity time should be provided by server
                webSocketConfig.connConfig.urlConnValidTime = Date.now() + WEBSOCKET_URL_VALID_TIME_MS;
                return initWebSocket();
            },
                function (reason) {
                    webSocketConfig.promiseCompleted = true;
                    sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_FAILURE, reason));

                    // If our connection fails because of network failure, we want to retry
                    if (Utils.isNetworkFailure(reason)) {
                        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_RETRY, reason));
                        reconnectionClient.retry();
                    } else {
                        // If we're not going to retry, we should terminate WSM
                        terminateWebSocketManager("Failed to fetch webSocket connection configuration: " + JSON.stringify(reason));
                    }
                    return { webSocketConnectionFailed: true };
                });
    };

    const initWebSocket = function () {
        if (reconnectConfig.websocketInitFailed) {
            sendInternalLogToServer(logger.info("web-socket initializing had failed, aborting re-init"));

            return { webSocketConnectionFailed: true };
        }
        if (!Utils.isNetworkOnline()) {
            sendInternalLogToServer(logger.warn("System is offline aborting web-socket init"));

            return { webSocketConnectionFailed: true };
        }
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_INIT));

        printWebSocketState("initWebSocket");
        try {
            if (validWebSocketConnConfig(webSocketConfig.connConfig)) {
                let ws = null;
                if (isWebSocketOpen(webSocket.primary)) {
                    sendInternalLogToServer(logger.debug("Primary Socket connection is already open"));

                    if (!isWebSocketState(webSocket.secondary, WebSocket.CONNECTING)) {
                        sendInternalLogToServer(logger.debug("Establishing a secondary web-socket connection"));
                        reconnectionClient.numAttempts = 0;
                        webSocket.secondary = getNewWebSocket();
                    }
                    ws = webSocket.secondary;
                } else {
                    if (!isWebSocketState(webSocket.primary, WebSocket.CONNECTING)) {
                        sendInternalLogToServer(logger.debug("Establishing a primary web-socket connection"));

                        webSocket.primary = getNewWebSocket();
                    }
                    ws = webSocket.primary;
                }

                // WebSocket creation is async task hence we Wait for 1sec before any potential retry
                reconnectConfig.webSocketInitCheckerTimeoutId = setTimeout(function () {
                    if (!isWebSocketOpen(ws)) {
                        retryWebSocketInitialization();
                    }
                }, 1000);
                return { webSocketConnectionFailed: false };
            }
        } catch (error) {
            sendInternalLogToServer(logger.error("Error Initializing web-socket-manager", error));

            terminateWebSocketManager("Failed to initialize new WebSocket: " + error.message);
            return { webSocketConnectionFailed: true };
        }
    };

    const getNewWebSocket = function () {
        let ws = new WebSocket(webSocketConfig.connConfig.webSocketTransport.url);
        ws.addEventListener("open", webSocketOnOpen);
        ws.addEventListener("message", webSocketOnMessage);
        ws.addEventListener("error", webSocketOnError);
        ws.addEventListener("close", event => webSocketOnClose(event, ws));
        return ws;
    };

    const onConnectionOpen = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_OPEN));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.connectionOpen.add(cb);
        return () => callbacks.connectionOpen.delete(cb);
    };

    const onConnectionClose = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_CLOSE));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.connectionClose.add(cb);
        return () => callbacks.connectionClose.delete(cb);
    };

    const onConnectionGain = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_GAIN));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.connectionGain.add(cb);
        if (isDefaultWebSocketOpen()) {
            cb();
        }
        return () => callbacks.connectionGain.delete(cb);
    };

    const onConnectionLost = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_CONNECTION_LOST));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.connectionLost.add(cb);
        if (reconnectConfig.connState === CONN_STATE.DISCONNECTED) {
            cb();
        }
        return () => callbacks.connectionLost.delete(cb);
    };

    const onInitFailure = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_INIT_FAILURE));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.initFailure.add(cb);
        if (reconnectConfig.websocketInitFailed) {
            cb();
        }
        return () => callbacks.initFailure.delete(cb);
    };

    const init = function (transportHandle) {
        Utils.assertTrue(Utils.isFunction(transportHandle), 'transportHandle must be a function');
        if (callbacks.getWebSocketTransport !== null) {
            sendInternalLogToServer(logger.warn("Web Socket Manager was already initialized"));
            return;
        }
        callbacks.getWebSocketTransport = transportHandle;

        return getWebSocketConnConfig();
    };

    const onSubscriptionUpdate = function (cb) {
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.subscriptionUpdate.add(cb);
        return () => callbacks.subscriptionUpdate.delete(cb);
    };

    const onSubscriptionFailure = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.WEBSOCKET_SUBSCRIPTION_FAILURE));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.subscriptionFailure.add(cb);
        return () => callbacks.subscriptionFailure.delete(cb);
    };

    const onMessage = function (topicName, cb) {
        Utils.assertNotNull(topicName, 'topicName');
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        if (callbacks.topic.has(topicName)) {
            callbacks.topic.get(topicName).add(cb);
        } else {
            callbacks.topic.set(topicName, new Set([cb]));
        }
        return () => callbacks.topic.get(topicName).delete(cb);
    };

    const onAllMessage = function (cb) {
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.allMessage.add(cb);
        return () => callbacks.allMessage.delete(cb);
    };

    const sendInternalLogToServer = function (logEntry) {
        if (logEntry && typeof logEntry.sendInternalLogToServer === "function")
            logEntry.sendInternalLogToServer();

        return logEntry;
    };

    const onDeepHeartbeatSuccess = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.DEEP_HEARTBEAT_SUCCESS));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.deepHeartbeatSuccess.add(cb);
        return () => callbacks.deepHeartbeatSuccess.delete(cb);
    };

    const onDeepHeartbeatFailure = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.DEEP_HEARTBEAT_FAILURE));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.deepHeartbeatFailure.add(cb);
        return () => callbacks.deepHeartbeatFailure.delete(cb);
    };

    const onTopicFailure = function (cb) {
        sendInternalLogToServer(logger.advancedLog(LOG_MESSAGES.TOPIC_FAILURE));
        Utils.assertTrue(Utils.isFunction(cb), 'cb must be a function');
        callbacks.topicFailure.add(cb);
        return () => callbacks.topicFailure.delete(cb);
    };

    this.init = init;
    this.onInitFailure = onInitFailure;
    this.onConnectionOpen = onConnectionOpen;
    this.onConnectionClose = onConnectionClose;
    this.onConnectionGain = onConnectionGain;
    this.onConnectionLost = onConnectionLost;
    this.onSubscriptionUpdate = onSubscriptionUpdate;
    this.onSubscriptionFailure = onSubscriptionFailure;
    this.onMessage = onMessage;
    this.onAllMessage = onAllMessage;
    this.subscribeTopics = subscribeTopics;
    this.sendMessage = sendMessage;
    this.onDeepHeartbeatSuccess = onDeepHeartbeatSuccess;
    this.onDeepHeartbeatFailure = onDeepHeartbeatFailure;
    this.onTopicFailure = onTopicFailure;

    this.closeWebSocket = function () {
        resetWebSocketState();
        resetSubscriptions();
        reconnectConfig.reconnectWebSocket = false;
        clearInterval(networkConnectivityChecker);
        closeWebSocket("User request to close WebSocket");
    };

    this.terminateWebSocketManager = terminateWebSocketManager;
};

const WebSocketManagerConstructor = (logMetaData) => {
    if (!logManagerInstance) {
        logManagerInstance = new LogManagerImpl(logMetaData);
    }
    if (!logManagerInstance.hasLogMetaData()) {
        logManagerInstance.setLogMetaData(logMetaData);
    }
    return new WebSocketManager();
};

const setGlobalConfig = config => {
    const loggerConfig = config && config.loggerConfig;
    if (!logManagerInstance) {
        logManagerInstance = new LogManagerImpl();
    }
    logManagerInstance.updateLoggerConfig(loggerConfig);

    const webSocketManagerConfig = config && config.webSocketManagerConfig;
    const customIsNetworkOnline = webSocketManagerConfig && webSocketManagerConfig.isNetworkOnline;
    if (customIsNetworkOnline && typeof customIsNetworkOnline === "function") {
        Utils.isNetworkOnline = customIsNetworkOnline;
    }
};

const WebSocketManagerObject = {
    create: WebSocketManagerConstructor,
    setGlobalConfig: setGlobalConfig,
    LogLevel: LogLevel,
    Logger: Logger,
};

export { WebSocketManagerObject };
